/**
 * API Client for TanStack Query Integration
 * 
 * Type-safe API client with automatic auth token injection,
 * error handling, and environment-based configuration.
 */

import { useAuthStore } from '@/stores/authStore';

export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export interface APIResponse<T> {
  data: T;
  meta?: {
    page?: number;
    perPage?: number;
    total?: number;
  };
}

export class APIClient {
  private baseURL: string;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  constructor(baseURL?: string) {
    this.baseURL = baseURL || import.meta.env.VITE_API_BASE_URL || '/api/v3';
  }

  /**
   * Execute a fetch request with the given token
   */
  private async doFetch(url: string, options: RequestInit, token: string | null): Promise<Response> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, { ...options, headers });
  }

  /**
   * Parse a successful response body
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    const jsonResponse = await response.json();

    // Unwrap standard API response format { success: true, data: {...} }
    if (jsonResponse && typeof jsonResponse === 'object' && 'success' in jsonResponse && 'data' in jsonResponse) {
      return jsonResponse.data;
    }

    return jsonResponse;
  }

  /**
   * Make an authenticated API request
   * Automatically injects auth token from Zustand store.
   * On 401, attempts a single token refresh and retries the request.
   *
   * NOTE: The API always returns HTTP 200 to prevent CloudFront from
   * intercepting error status codes. Errors are conveyed via
   * `{ success: false, error: { status, code, message } }` in the body.
   */
  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = useAuthStore.getState().accessToken;

    try {
      const response = await this.doFetch(url, options, token);

      // Non-200 HTTP status (shouldn't happen with our API, but handle edge cases)
      if (!response.ok) {
        await this.throwAPIError(response);
      }

      const jsonResponse = await response.json();

      // Check for API-level errors (success: false in body)
      if (jsonResponse && typeof jsonResponse === 'object' && jsonResponse.success === false) {
        const errorStatus = jsonResponse.error?.status ?? jsonResponse.status ?? 0;

        // If 401, try refreshing the token once and retry
        if (errorStatus === 401) {
          const newToken = await this.tryRefreshToken();
          if (newToken) {
            const retryResponse = await this.doFetch(url, options, newToken);
            const retryJson = await retryResponse.json();
            if (retryJson && typeof retryJson === 'object' && retryJson.success === false) {
              throw new APIError(
                retryJson.error?.message ?? 'Request failed',
                retryJson.error?.status ?? 0,
                retryJson.error?.code ?? 'UNKNOWN_ERROR',
                retryJson.error?.details,
              );
            }
            // Unwrap standard response
            if (retryJson && 'data' in retryJson) return retryJson.data;
            return retryJson;
          }
        }

        throw new APIError(
          jsonResponse.error?.message ?? jsonResponse.message ?? 'Request failed',
          errorStatus,
          jsonResponse.error?.code ?? 'UNKNOWN_ERROR',
          jsonResponse.error?.details,
        );
      }

      // Unwrap standard API response format { success: true, data: {...} }
      if (jsonResponse && typeof jsonResponse === 'object' && 'data' in jsonResponse) {
        return jsonResponse.data;
      }

      return jsonResponse;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      if (error instanceof TypeError) {
        throw new APIError(
          'Network error. Please check your connection.',
          0,
          'NETWORK_ERROR'
        );
      }

      throw new APIError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        0,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Attempt to refresh the access token.
   * Deduplicates concurrent refresh calls so only one request is made.
   * Returns the new token on success, or null if refresh fails.
   */
  private async tryRefreshToken(): Promise<string | null> {
    try {
      // Deduplicate: if a refresh is already in-flight, wait for it
      if (this.isRefreshing && this.refreshPromise) {
        await this.refreshPromise;
        return useAuthStore.getState().accessToken;
      }

      this.isRefreshing = true;
      this.refreshPromise = useAuthStore.getState().refreshAccessToken();
      await this.refreshPromise;
      return useAuthStore.getState().accessToken;
    } catch {
      // Refresh failed — user will need to log in again
      return null;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Extract error info from a non-OK response and throw an APIError
   */
  private async throwAPIError(response: Response): Promise<never> {
    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    throw new APIError(
      errorData.error?.message || errorData.message || 'Request failed',
      response.status,
      errorData.error?.code || 'UNKNOWN_ERROR',
      errorData.error?.details || errorData.details
    );
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    let queryString = '';
    if (params) {
      // Strip undefined/null/empty-string values so they don't become literal "undefined" in the URL
      const cleaned = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
      );
      const qs = new URLSearchParams(cleaned).toString();
      if (qs) queryString = '?' + qs;
    }
    
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new APIClient();
